/* ==========================================================================
   DEMO ONLY: Display Preferences Panel & Shared Hook
   ========================================================================== */
import React, { FunctionComponent, useEffect, useState } from 'react'
import styles from './DemoOptionsPanel.module.css'
import { GUIDE_SEEN_KEY, hasSeenGuide } from './BriefGuide'
import { HubBannerStyle } from './IntelligenceHubScene'
import { FleetValueFormulaStyle } from './FleetValueFormula'

export interface DemoOptions {
  showOverviewKeyInsights: boolean
  showAllOverviewBriefs: boolean
  briefTicketStub: boolean
  fleetValueFormula: FleetValueFormulaStyle
  showHubMetrics: boolean
  showHubPitch: boolean
  hubBannerStyle: HubBannerStyle
  tourStyle: TourStyle
  assistantName: AssistantName
  briefsView: BriefsView
  showComingSoonBriefs: boolean
}

/* how the Briefs tab lays out its briefs: the lifecycle board, a list or shelves */
export type BriefsView = 'board' | 'list' | 'shelf'

const BRIEFS_VIEWS: { value: BriefsView; label: string }[] = [
  { value: 'board', label: 'Lifecycle' },
  { value: 'list', label: 'List' },
  { value: 'shelf', label: 'Library' },
]

/* what the brief's AI assistant is called: its panel heading, and the matching tour step */
export type AssistantName = 'mission-control' | 'help' | 'partner' | 'assistant' | 'anything'

export const ASSISTANT_NAMES: { value: AssistantName; label: string; tourTitle: string }[] = [
  { value: 'mission-control', label: 'Mission Control', tourTitle: 'Ask Mission Control' },
  { value: 'help', label: 'How can I help?', tourTitle: 'Ask a question' },
  { value: 'partner', label: 'Ask Partner Intelligence', tourTitle: 'Ask Partner Intelligence' },
  { value: 'assistant', label: 'Brief assistant', tourTitle: 'Ask the brief assistant' },
  { value: 'anything', label: 'Ask me anything', tourTitle: 'Ask me anything' },
]

export const assistantNameFor = (value: AssistantName) =>
  ASSISTANT_NAMES.find(name => name.value === value) ?? ASSISTANT_NAMES[0]

export type TourStyle = 'highlights' | 'diagram'

const TOUR_STYLES: { value: TourStyle; label: string }[] = [
  { value: 'highlights', label: 'Highlights' },
  { value: 'diagram', label: 'Diagram' },
]

const FORMULA_STYLES: { value: FleetValueFormulaStyle; label: string }[] = [
  { value: 'formula', label: 'Formula' },
  { value: 'text', label: 'Text' },
]

const BANNER_STYLES: { value: HubBannerStyle; label: string }[] = [
  { value: 'orbit', label: 'Orbit' },
  { value: 'pipeline', label: 'Circuit' },
  { value: 'horizon', label: 'Horizon' },
  { value: 'colony', label: 'Colony' },
]

const DEFAULT_OPTIONS: DemoOptions = {
  showOverviewKeyInsights: false,
  showAllOverviewBriefs: false,
  briefTicketStub: true,
  fleetValueFormula: 'formula',
  showHubMetrics: true,
  showHubPitch: true,
  hubBannerStyle: 'pipeline', // Circuit
  tourStyle: 'diagram',
  assistantName: 'anything',
  briefsView: 'board',
  showComingSoonBriefs: true,
}

/* saved options over the defaults; a fleet value formula style that's since been retired reads as the formula */
const withDefaults = (saved: Partial<DemoOptions>): DemoOptions => {
  const options = { ...DEFAULT_OPTIONS, ...saved }
  return options.fleetValueFormula === 'text' ? options : { ...options, fleetValueFormula: 'formula' }
}

const STORAGE_KEY = 'intelligence_hub_demo_options'
const UPDATE_EVENT = 'intelligence_hub_demo_options_changed'

export function useDemoOptions() {
  const [options, setOptions] = useState<DemoOptions>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? withDefaults(JSON.parse(saved)) : DEFAULT_OPTIONS
    } catch {
      return DEFAULT_OPTIONS
    }
  })

  useEffect(() => {
    const handleUpdate = () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        setOptions(saved ? withDefaults(JSON.parse(saved)) : DEFAULT_OPTIONS)
      } catch {}
    }
    window.addEventListener(UPDATE_EVENT, handleUpdate)
    return () => window.removeEventListener(UPDATE_EVENT, handleUpdate)
  }, [])

  const updateOption = <K extends keyof DemoOptions>(key: K, value: DemoOptions[K]) => {
    const updated = { ...options, [key]: value }
    setOptions(updated)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
      window.dispatchEvent(new Event(UPDATE_EVENT))
    } catch {}
  }

  // back to the defaults: the saved choices are dropped, so later changes to the defaults apply too
  const resetOptions = () => {
    setOptions(DEFAULT_OPTIONS)
    try {
      localStorage.removeItem(STORAGE_KEY)
      window.dispatchEvent(new Event(UPDATE_EVENT))
    } catch {}
  }

  const isDefault = (Object.keys(DEFAULT_OPTIONS) as (keyof DemoOptions)[]).every(
    key => options[key] === DEFAULT_OPTIONS[key]
  )

  return { options, updateOption, resetOptions, isDefault }
}

export const DemoOptionsPanel: FunctionComponent = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { options, updateOption, resetOptions, isDefault } = useDemoOptions()
  const [guideSeen, setGuideSeen] = useState(hasSeenGuide)

  const resetGuide = () => {
    try {
      localStorage.removeItem(GUIDE_SEEN_KEY)
    } catch {}
    setGuideSeen(false)
  }

  return (
    <div className={`${styles.demoWidgetWrapper}${isExpanded ? ` ${styles.expanded}` : ''}`}>
      {!isExpanded ? (
        <button
          type='button'
          className={styles.triggerButton}
          onClick={() => setIsExpanded(true)}
          aria-label='Open display preferences'
          title='Display preferences'
        >
          <svg viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' strokeWidth='2'>
            <line x1='4' y1='21' x2='4' y2='14' />
            <line x1='4' y1='10' x2='4' y2='3' />
            <line x1='12' y1='21' x2='12' y2='12' />
            <line x1='12' y1='8' x2='12' y2='3' />
            <line x1='20' y1='21' x2='20' y2='16' />
            <line x1='20' y1='12' x2='20' y2='3' />
            <line x1='1' y1='14' x2='7' y2='14' />
            <line x1='9' y1='8' x2='15' y2='8' />
            <line x1='17' y1='16' x2='23' y2='16' />
          </svg>
        </button>
      ) : (
        <div className={styles.overlayPanel}>
          <header className={styles.panelHeader}>
            <h4>Display preferences</h4>
            <div className={styles.headerActions}>
              <button
                type='button'
                className={`${styles.closeButton} ${styles.resetButton}`}
                onClick={resetOptions}
                disabled={isDefault}
                aria-label='Reset to defaults'
                title='Reset to defaults'
              >
                <svg
                  viewBox='0 0 24 24'
                  width='16'
                  height='16'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M3 12a9 9 0 1 0 3-6.7' />
                  <path d='M3 3v6h6' />
                </svg>
              </button>
              <button
                type='button'
                className={styles.closeButton}
                onClick={() => setIsExpanded(false)}
                aria-label='Close options panel'
              >
                <svg viewBox='0 0 24 24' width='18' height='18' fill='none' stroke='currentColor' strokeWidth='2'>
                  <line x1='18' y1='6' x2='6' y2='18' />
                  <line x1='6' y1='6' x2='18' y2='18' />
                </svg>
              </button>
            </div>
          </header>

          <div className={styles.panelBody}>
            {/* Group 1: Intelligence Hub Main Landing Page */}
            <div className={styles.groupSection}>
              <h5 className={styles.groupHeader}>Intelligence Hub</h5>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Pitch banner</strong>
                  <small>Show intro pitch banner at top of Hub</small>
                </div>
                <input
                  type='checkbox'
                  checked={options.showHubPitch}
                  onChange={e => updateOption('showHubPitch', e.target.checked)}
                  className={styles.checkbox}
                />
              </label>

              {/* which pitch banner: a segmented toggle under its switch */}
              <div className={styles.segmented} role='group' aria-label='Pitch banner style'>
                {BANNER_STYLES.map(style => (
                  <button
                    key={style.value}
                    type='button'
                    className={options.hubBannerStyle === style.value ? styles.segmentActive : styles.segment}
                    aria-pressed={options.hubBannerStyle === style.value}
                    disabled={!options.showHubPitch}
                    onClick={() => updateOption('hubBannerStyle', style.value)}
                  >
                    {style.label}
                  </button>
                ))}
              </div>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Metric cards</strong>
                  <small>Show total devices, volume, risk flags &amp; fleet value</small>
                </div>
                <input
                  type='checkbox'
                  checked={options.showHubMetrics}
                  onChange={e => updateOption('showHubMetrics', e.target.checked)}
                  className={styles.checkbox}
                />
              </label>
            </div>

            {/* Group 2: Customer Tab */}
            <div className={styles.groupSection}>
              <h5 className={styles.groupHeader}>Customer</h5>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Overview key insights</strong>
                  <small>Show insights &amp; suggestions alongside highlights on overview tab</small>
                </div>
                <input
                  type='checkbox'
                  checked={options.showOverviewKeyInsights}
                  onChange={e => updateOption('showOverviewKeyInsights', e.target.checked)}
                  className={styles.checkbox}
                />
              </label>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Brief pager on overview</strong>
                  <small>
                    Page between the latest Health check and Fleet makeup (up to 3 briefs), not just the Health check
                  </small>
                </div>
                <input
                  type='checkbox'
                  checked={options.showAllOverviewBriefs}
                  onChange={e => updateOption('showAllOverviewBriefs', e.target.checked)}
                  className={styles.checkbox}
                />
              </label>

              <div className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Fleet value formula</strong>
                  <small>How the Intel tab explains estimated fleet value</small>
                </div>
              </div>
              <div className={styles.segmented} role='group' aria-label='Fleet value formula style'>
                {FORMULA_STYLES.map(style => (
                  <button
                    key={style.value}
                    type='button'
                    className={options.fleetValueFormula === style.value ? styles.segmentActive : styles.segment}
                    aria-pressed={options.fleetValueFormula === style.value}
                    onClick={() => updateOption('fleetValueFormula', style.value)}
                  >
                    {style.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Group 3: Briefs tab */}
            <div className={styles.groupSection}>
              <h5 className={styles.groupHeader}>Briefs tab</h5>

              <div className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Layout</strong>
                  <small>The lifecycle board, a list of every brief, or shelves of them by stage</small>
                </div>
              </div>
              <div className={styles.segmented} role='group' aria-label='Briefs layout'>
                {BRIEFS_VIEWS.map(view => (
                  <button
                    key={view.value}
                    type='button'
                    className={options.briefsView === view.value ? styles.segmentActive : styles.segment}
                    aria-pressed={options.briefsView === view.value}
                    onClick={() => updateOption('briefsView', view.value)}
                  >
                    {view.label}
                  </button>
                ))}
              </div>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Coming soon briefs</strong>
                  <small>
                    Show every coming-soon brief. Off, each wallet folds them into one "+N coming soon" card
                  </small>
                </div>
                <input
                  type='checkbox'
                  checked={options.showComingSoonBriefs}
                  onChange={e => updateOption('showComingSoonBriefs', e.target.checked)}
                  className={styles.checkbox}
                />
              </label>
            </div>

            {/* Group 4: Brief */}
            <div className={styles.groupSection}>
              <h5 className={styles.groupHeader}>Brief</h5>

              <label className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Ticket stub headline</strong>
                  <small>
                    Show the headline numbers as a perforated stub across the top of the brief, with an AI stamp
                  </small>
                </div>
                <input
                  type='checkbox'
                  checked={options.briefTicketStub}
                  onChange={e => updateOption('briefTicketStub', e.target.checked)}
                  className={styles.checkbox}
                />
              </label>

              <div className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong>Tour</strong>
                  <small>
                    {guideSeen ? 'Show the tour again on the next brief opened' : 'Opens on the next brief'}
                  </small>
                </div>
                <button type='button' className='btn secondary sml' onClick={resetGuide} disabled={!guideSeen}>
                  Reset
                </button>
              </div>

              {/* how the tour shows: spotlighting each area in turn, or one diagram above the brief */}
              <div className={styles.segmented} role='group' aria-label='Tour style'>
                {TOUR_STYLES.map(style => (
                  <button
                    key={style.value}
                    type='button'
                    className={options.tourStyle === style.value ? styles.segmentActive : styles.segment}
                    aria-pressed={options.tourStyle === style.value}
                    onClick={() => updateOption('tourStyle', style.value)}
                  >
                    {style.label}
                  </button>
                ))}
              </div>

              <div className={styles.toggleRow}>
                <div className={styles.toggleLabel}>
                  <strong id='demo-assistant-name'>AI assistant</strong>
                  <small>What the assistant beside the brief is called</small>
                </div>
              </div>
              <div className={`select sml ${styles.selectWrap}`}>
                <select
                  aria-labelledby='demo-assistant-name'
                  value={options.assistantName}
                  onChange={e => updateOption('assistantName', e.target.value as AssistantName)}
                >
                  {ASSISTANT_NAMES.map(name => (
                    <option key={name.value} value={name.value}>
                      {name.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
